import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';
import { MarkdownTable } from '../../../components/MarkdownTable';
import { stripSemicolonsBeforeFirstCitation } from '../../../utils/citationDisplay';
import { collectMarkdownTableLines } from '../../../utils/markdownTable';
import { findSourceIndex, normalizeDocHint, normalizeSerffId, sourceDocKey } from '../answersService';

const CITATION_REGEX = /(?:\[\s*)?(?:SERFF[-\u2010-\u2015])?([A-Z]+[-\u2010-\u2015]\d+)(?:,\s*([^\],;]+))?,\s*(?:Pages?|pages?|pp?\.)\s*(\d+)(?:[-\u2010-\u2015,\s]+(\d+))?(?:\s*\])?/g;
const PAGES_CONTINUATION_REGEX = /[;,]?\s*(?:Pages?|pages?|pp?\.)\s*(\d+)(?:[-\u2010-\u2015]\s*(\d+))?/g;
const PAGES_CONTINUATION_SHORT_REGEX = /,\s*(\d+)(?:[-\u2010-\u2015]\s*(\d+))?/g;
const PAGES_CONTINUATION_SEMICOLON_REGEX = /;\s*(\d+)(?:[-\u2010-\u2015]\s*(\d+))?/g;

const normalizeForSearch = (value) => String(value || '').trim().replace(/\s+/g, ' ').replace(/^["''`]+|["''`]+$/g, '').trim();

const buildPageSpanIndex = (spans) => {
  const ranges = [];
  let pos = 0;
  const parts = [];
  for (const span of spans) {
    const text = normalizeForSearch(span.textContent || '');
    const start = pos;
    if (parts.length) pos += 1;
    parts.push(text);
    pos += text.length;
    ranges.push({ span, start, end: pos });
  }
  return { fullNorm: parts.join(' '), ranges };
};

const normRangeForCompactSlice = (fullNorm, compactStart, compactLength) => {
  if (compactLength <= 0) return null;
  let compactPos = 0;
  let start = -1;
  for (let i = 0; i < fullNorm.length; i += 1) {
    if (fullNorm[i] === ' ') continue;
    if (compactPos === compactStart) start = i;
    compactPos += 1;
    if (compactPos === compactStart + compactLength) {
      return start >= 0 ? { start, end: i + 1 } : null;
    }
  }
  return start >= 0 ? { start, end: fullNorm.length } : null;
};

const findCompactSnippetRangeInFullNorm = (fullNorm, searchText, minLength) => {
  const normSearch = normalizeForSearch(searchText).replace(/\s/g, '');
  if (normSearch.length < minLength) return null;
  const normPage = fullNorm.replace(/\s/g, '');
  const take = Math.min(120, normSearch.length);
  const needle = normSearch.slice(0, take);
  const compactIndex = normPage.indexOf(needle);
  if (compactIndex < 0) return null;
  return normRangeForCompactSlice(fullNorm, compactIndex, take);
};

const citationLinkTooltip = (serffId, docHint, pageStart, pageEnd) => {
  const pages = pageStart === pageEnd ? `${pageStart}` : `${pageStart}-${pageEnd}`;
  return docHint?.trim()
    ? `[${serffId}, ${docHint.trim()}, Pages ${pages}]`
    : `[${serffId}, Pages ${pages}]`;
};

const collectCitationMatches = (text) => {
  const matches = [];
  CITATION_REGEX.lastIndex = 0;
  let match;
  while ((match = CITATION_REGEX.exec(text)) !== null) {
    const pageStart = parseInt(match[3], 10);
    const pageEnd = match[4] != null && match[4] !== '' ? parseInt(match[4], 10) : pageStart;
    const serffId = normalizeSerffId(match[1]);
    const docHint = normalizeDocHint(match[2]);
    matches.push({
      index: match.index,
      serffId,
      docHint,
      pageStart,
      pageEnd,
      full: match[0],
    });

    const afterFull = match.index + match[0].length;
    let blockEnd = text.length;
    const nextClose = text.indexOf(']', afterFull);
    if (nextClose >= 0) blockEnd = Math.min(blockEnd, nextClose + 1);
    const nextOpen = text.indexOf('[', afterFull);
    if (nextOpen >= 0) blockEnd = Math.min(blockEnd, nextOpen);
    const block = text.slice(afterFull, blockEnd);

    const addContinuationMatches = (regex) => {
      regex.lastIndex = 0;
      let pageMatch;
      while ((pageMatch = regex.exec(block)) !== null) {
        const continuationStart = parseInt(pageMatch[1], 10);
        const continuationEnd = pageMatch[2] != null && pageMatch[2] !== '' ? parseInt(pageMatch[2], 10) : continuationStart;
        matches.push({
          index: afterFull + pageMatch.index,
          serffId,
          docHint,
          pageStart: continuationStart,
          pageEnd: continuationEnd,
          full: pageMatch[0],
        });
      }
    };

    addContinuationMatches(PAGES_CONTINUATION_REGEX);
    addContinuationMatches(PAGES_CONTINUATION_SHORT_REGEX);
    addContinuationMatches(PAGES_CONTINUATION_SEMICOLON_REGEX);
  }

  matches.sort((a, b) => a.index - b.index);
  const deduped = [];
  let lastEnd = -1;
  for (const item of matches) {
    if (item.index >= lastEnd) {
      deduped.push(item);
      lastEnd = item.index + item.full.length;
    }
  }
  return deduped;
};

const citationIdentityKey = (match) => (
  `${match.serffId}|${match.docHint || ''}|${match.pageStart}|${match.pageEnd}`
);

const parseInlineCitations = (text, turn, onCitationClick, options = {}) => {
  const out = [];
  const sources = turn?.response?.sources || [];
  const input = options.isSourceColumn ? stripSemicolonsBeforeFirstCitation(text) : String(text || '');
  const matches = collectCitationMatches(input);
  if (!matches.length) return [input];

  let last = 0;
  const bodyParts = [];
  const citationButtons = [];
  const seenCitationKeys = new Set();

  for (const match of matches) {
    if (match.index > last) {
      bodyParts.push(input.slice(last, match.index));
    }
    const identityKey = citationIdentityKey(match);
    if (options.dedupeCitations && seenCitationKeys.has(identityKey)) {
      last = match.index + match.full.length;
      continue;
    }
    if (options.dedupeCitations) seenCitationKeys.add(identityKey);

    const sourceIndex = findSourceIndex(sources, match.serffId, match.pageStart, match.pageEnd, match.docHint);
    const tooltip = citationLinkTooltip(match.serffId, match.docHint, match.pageStart, match.pageEnd);
    citationButtons.push(
      <button
        type="button"
        key={`${match.index}-${identityKey}`}
        className="answers-md-citation"
        title={tooltip}
        aria-label={`Open source: ${tooltip}`}
        onClick={() => onCitationClick(turn, { ...match, sourceIndex })}
      >
        Source
      </button>,
    );
    last = match.index + match.full.length;
  }
  if (last < input.length) bodyParts.push(input.slice(last));

  let bodyText = bodyParts
    .join('')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();

  if (options.isSourceColumn) {
    bodyText = bodyText.replace(/^[\s;]+/, '');
    if (!bodyText.replace(/[\s;]+/g, '')) bodyText = '';
  }

  if (bodyText) out.push(bodyText);
  if (citationButtons.length) {
    if (bodyText) out.push(' ');
    citationButtons.forEach((btn, idx) => {
      if (idx > 0) out.push(' ');
      out.push(btn);
    });
  }
  return out;
};

export const MarkdownLite = ({ text, turn, onCitationClick }) => {
  const parseInlineMarkdown = (value, keyPrefix = 'md') => {
    const input = String(value || '');
    if (!input) return '';

    const out = [];
    const tokenRegex = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;
    let lastIndex = 0;
    let match;
    let tokenIndex = 0;

    while ((match = tokenRegex.exec(input)) !== null) {
      if (match.index > lastIndex) {
        out.push(input.slice(lastIndex, match.index));
      }
      const token = match[0];
      if (token.startsWith('**') && token.endsWith('**')) {
        out.push(<strong key={`${keyPrefix}-b-${tokenIndex}`}>{token.slice(2, -2)}</strong>);
      } else if (token.startsWith('`') && token.endsWith('`')) {
        out.push(<code key={`${keyPrefix}-c-${tokenIndex}`}>{token.slice(1, -1)}</code>);
      } else if (token.startsWith('*') && token.endsWith('*')) {
        out.push(<em key={`${keyPrefix}-i-${tokenIndex}`}>{token.slice(1, -1)}</em>);
      } else {
        out.push(token);
      }
      tokenIndex += 1;
      lastIndex = match.index + token.length;
    }

    if (lastIndex < input.length) {
      out.push(input.slice(lastIndex));
    }
    return out;
  };

  const parseLineContent = (line, lineIdx, contentOptions = {}) => {
    const cited = parseInlineCitations(line, turn, onCitationClick, contentOptions);
    return cited.flatMap((part, partIdx) => (
      typeof part === 'string'
        ? parseInlineMarkdown(part, `ln-${lineIdx}-p-${partIdx}`)
        : [part]
    ));
  };

  const isCitationOnlyLine = (line) => {
    const raw = String(line || '');
    if (!raw.trim()) return false;
    const matches = collectCitationMatches(raw);
    if (!matches.length) return false;

    let remainder = raw;
    matches.forEach((match) => {
      remainder = remainder.replace(match.full, ' ');
    });

    return remainder.replace(/[\s,;[\]()]+/g, '').length === 0;
  };

  const isStandaloneTitleLine = (trimmed, nextRaw) => {
    if (!trimmed || !nextRaw) return false;
    if (trimmed.length > 120) return false;
    if (/^(?:---|\*\*\*|___)\s*$/.test(trimmed)) return false;
    if (trimmed.startsWith('#') || trimmed.startsWith('> ')) return false;
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) return false;
    if (/^\d+\.\s+/.test(trimmed)) return false;
    if (isCitationOnlyLine(trimmed)) return false;
    if (/[.:!?]$/.test(trimmed)) return false;
    return !String(nextRaw || '').trim();
  };

  const isCitationsHeadingLine = (line) => {
    const normalized = String(line || '')
      .trim()
      .replace(/^#{1,6}\s+/, '')
      .replace(/^[*-]\s+/, '')
      .replace(/^\d+\.\s+/, '')
      .replace(/^\*\*([^*]+)\*\*$/, '$1')
      .replace(/^__([^_]+)__$/, '$1')
      .replace(/[:\s]+$/g, '')
      .toLowerCase();
    return normalized === 'citations';
  };

  const isCitationSectionItemLine = (line) => {
    const raw = String(line || '');
    const trimmed = raw.trim();
    if (!trimmed) return true;
    if (isCitationOnlyLine(raw)) return true;
    return /^[-*]\s+source\s*$/i.test(trimmed) || /^\d+\.\s+source\s*$/i.test(trimmed);
  };

  const renderMarkdownTable = (tableLines, tableKey) => (
    <MarkdownTable
      tableLines={tableLines}
      tableKey={tableKey}
      wrapClassName="answers-md-table-wrap"
      tableClassName="answers-md-table"
      renderCell={(cell, cellKey, meta) => parseLineContent(cell, cellKey, meta)}
    />
  );

  // Parse citations from raw answer text (same as the-comply-ai-ui linkify on DOM text).
  // Escaping underscores inside brackets breaks doc hints and RAG file_name_hint matching.
  const lines = (text || '').split('\n');
  const rendered = [];
  let skippingCitationsSection = false;
  for (let idx = 0; idx < lines.length; idx += 1) {
    const raw = lines[idx] || '';
    const t = raw.trim();
    const nextRaw = idx + 1 < lines.length ? lines[idx + 1] : '';

    if (isCitationsHeadingLine(raw)) {
      skippingCitationsSection = true;
      continue;
    }
    if (skippingCitationsSection) {
      if (isCitationSectionItemLine(raw)) continue;
      skippingCitationsSection = false;
    }

    if (!t) {
      rendered.push(<div key={idx} className="answers-md-spacer" />);
      continue;
    }
    const tableBlock = collectMarkdownTableLines(lines, idx);
    if (tableBlock) {
      rendered.push(renderMarkdownTable(tableBlock.tableLines, `tbl-${idx}`));
      idx = tableBlock.endIndex - 1;
      continue;
    }
    if (isCitationOnlyLine(raw)) {
      const citationLines = [raw.trim()];
      let cursor = idx + 1;
      while (cursor < lines.length && isCitationOnlyLine(lines[cursor])) {
        citationLines.push(lines[cursor].trim());
        cursor += 1;
      }
      rendered.push(
        <p key={`cit-${idx}`} className="answers-md-citation-group">
          {parseLineContent(citationLines.join(' '), idx)}
        </p>
      );
      idx = cursor - 1;
      continue;
    }
    if (isStandaloneTitleLine(t, nextRaw)) {
      rendered.push(<h3 key={idx}>{parseLineContent(t, idx)}</h3>);
      continue;
    }
    if (/^(?:---|\*\*\*|___)\s*$/.test(t)) {
      rendered.push(<hr key={idx} className="answers-md-hr" />);
      continue;
    }
    if (t.startsWith('> ')) {
      rendered.push(<blockquote key={idx}>{parseLineContent(t.slice(2), idx)}</blockquote>);
      continue;
    }
    if (t.startsWith('### ')) {
      rendered.push(<h3 key={idx}>{parseLineContent(t.slice(4), idx)}</h3>);
      continue;
    }
    if (t.startsWith('## ')) {
      rendered.push(<h2 key={idx}>{parseLineContent(t.slice(3), idx)}</h2>);
      continue;
    }
    if (t.startsWith('# ')) {
      rendered.push(<h1 key={idx}>{parseLineContent(t.slice(2), idx)}</h1>);
      continue;
    }
    if (t.startsWith('- ') || t.startsWith('* ')) {
      rendered.push(
        <p key={idx} className="answers-md-list-item">
          <span className="answers-md-list-bullet" aria-hidden="true">•</span>
          {parseLineContent(t.slice(2), idx)}
        </p>
      );
      continue;
    }
    if (/^\d+\.\s+/.test(t)) {
      rendered.push(<p key={idx} className="answers-md-list-item">{parseLineContent(t, idx)}</p>);
      continue;
    }
    rendered.push(<p key={idx}>{parseLineContent(raw, idx)}</p>);
  }

  return (
    <>
      {rendered}
    </>
  );
};

const getTurnForSelectedSource = (conversation, selectedSource) => {
  if (!selectedSource) return undefined;
  const byRef = conversation.find((turn) => turn.response?.sources?.includes(selectedSource));
  if (byRef) return byRef;
  const key = sourceDocKey(selectedSource);
  if (!key) return undefined;
  const pageStart = selectedSource.page_start ?? 0;
  const pageEnd = selectedSource.page_end ?? pageStart;
  return conversation.find((turn) => {
    const sources = turn.response?.sources || [];
    return sources.some((source) => {
      if (sourceDocKey(source) !== key) return false;
      const sourceStart = source.page_start ?? 0;
      const sourceEnd = source.page_end ?? sourceStart;
      return !(sourceEnd < pageStart || sourceStart > pageEnd);
    });
  });
};

const getViewportRectsForSource = (pageViewport, pageNumber, source) => {
  if (!pageViewport?.convertToViewportRectangle || !source?.coordinates?.length) return [];
  const coordinates = source.coordinates.filter((coord) => coord.page === pageNumber);
  const rects = [];
  for (const coord of coordinates) {
    const bbox = coord?.bbox;
    if (!bbox || bbox.length < 4) continue;
    const rect = pageViewport.convertToViewportRectangle([bbox[0], bbox[1], bbox[2], bbox[3]]);
    if (!rect || rect.length < 4) continue;
    rects.push({
      left: Math.min(rect[0], rect[2]),
      top: Math.min(rect[1], rect[3]),
      width: Math.abs(rect[2] - rect[0]),
      height: Math.abs(rect[3] - rect[1]),
    });
  }
  return rects;
};

const drawCoordinateFallbackOverlays = (pageEl, pageViewport, pageNumber, sources) => {
  if (!pageEl) return;
  pageEl.querySelectorAll('.citation-highlight').forEach((el) => el.remove());
  for (const source of sources) {
    const rects = getViewportRectsForSource(pageViewport, pageNumber, source);
    for (const rect of rects) {
      const highlight = document.createElement('div');
      highlight.className = 'citation-highlight';
      highlight.style.left = `${rect.left}px`;
      highlight.style.top = `${rect.top}px`;
      highlight.style.width = `${rect.width}px`;
      highlight.style.height = `${rect.height}px`;
      pageEl.appendChild(highlight);
    }
  }
};
export const PdfCitationViewer = ({ pdfUrl, selectedSource, conversation, onLoadError, onLoadSuccess }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(selectedSource?.page_start || 1);
  const [scale, setScale] = useState(1.1);
  const [pageReady, setPageReady] = useState(false);
  const [renderError, setRenderError] = useState('');
  const canvasRef = useRef(null);
  const pageRef = useRef(null);
  const textLayerRef = useRef(null);
  const retryRef = useRef(new Map());

  const clearRenderedPage = () => {
    const canvas = canvasRef.current;
    const pageEl = pageRef.current;
    const textLayer = textLayerRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;
      canvas.style.width = '';
      canvas.style.height = '';
    }
    if (pageEl) {
      pageEl.style.width = '';
      pageEl.style.height = '';
      pageEl.querySelectorAll('.citation-highlight').forEach((el) => el.remove());
    }
    textLayer?.replaceChildren();
  };

  useEffect(() => {
    setPageNumber(selectedSource?.page_start || 1);
    setPageReady(false);
    clearRenderedPage();
    retryRef.current.clear();
  }, [selectedSource]);

  useEffect(() => {
    if (!pdfUrl) return undefined;
    let cancelled = false;
    clearRenderedPage();
    setPdfDoc(null);
    setNumPages(0);
    setPageReady(false);
    setRenderError('');
    retryRef.current.clear();
    const task = pdfjsLib.getDocument({ url: pdfUrl });
    task.promise
      .then((doc) => {
        if (cancelled) {
          return;
        }
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setPageNumber((current) => Math.min(Math.max(current || selectedSource?.page_start || 1, 1), doc.numPages));
        onLoadSuccess?.();
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error?.message || 'This PDF could not be loaded. It may be unavailable or you may not have access.';
        setRenderError(message);
        onLoadError?.(message);
      });
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, selectedSource?.page_start, onLoadError, onLoadSuccess]);

  const sameDocSources = useMemo(() => {
    if (!selectedSource) return [];
    const selectedKey = sourceDocKey(selectedSource);
    const turn = getTurnForSelectedSource(conversation, selectedSource);
    const candidates = turn?.response?.sources || [selectedSource];
    return candidates.filter((source) => sourceDocKey(source) === selectedKey);
  }, [conversation, selectedSource]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !pageRef.current || !textLayerRef.current) return undefined;
    let cancelled = false;
    let renderTask = null;
    setPageReady(false);
    clearRenderedPage();

    const clearPageLayers = () => {
      const pageEl = pageRef.current;
      const textLayer = textLayerRef.current;
      if (!pageEl || !textLayer) return;
      pageEl.querySelectorAll('.citation-highlight').forEach((el) => el.remove());
      textLayer.replaceChildren();
    };

    const applyTextAndCoordinateHighlights = (pageViewport, currentPageNumber) => {
      const pageEl = pageRef.current;
      const textLayer = textLayerRef.current;
      if (!pageEl || !textLayer) return;
      // Match comply-ui: bbox boxes are a fallback before text layer is ready.
      // Once text spans exist, remove boxes and apply text-span highlights only.
      pageEl.querySelectorAll('.citation-highlight').forEach((el) => el.remove());
      textLayer.querySelectorAll('.citation-highlight-text').forEach((el) => el.classList.remove('citation-highlight-text'));

      const sourcesForPage = sameDocSources.filter((source) => {
        const sourceStart = source.page_start ?? 0;
        const sourceEnd = source.page_end ?? sourceStart;
        return (sourceStart <= currentPageNumber && currentPageNumber <= sourceEnd) ||
          (source.coordinates?.length && source.coordinates.some((coord) => coord.page === currentPageNumber));
      });
      if (!sourcesForPage.length) return;

      const spans = Array.from(textLayer.querySelectorAll('span'));
      const pageRect = pageEl.getBoundingClientRect();
      const highlighted = new Set();
      const { fullNorm, ranges } = buildPageSpanIndex(spans);

      if (fullNorm.length > 0) {
        for (const source of sourcesForPage) {
          const searchText = normalizeForSearch(source.text || '');
          if (!searchText) continue;
          const snippets = [
            searchText.length > 180 ? searchText.slice(0, 180) : searchText,
            searchText.length > 80 ? searchText.slice(0, 80) : searchText,
            searchText.length > 80 ? searchText.slice(-80) : searchText,
          ];
          const words = searchText.split(/\s+/).filter(Boolean);
          if (words.length >= 5) snippets.push(words.slice(0, 5).join(' '));
          if (words.length >= 3) snippets.push(words.slice(0, 3).join(' '));

          let matched = false;
          for (const snippet of snippets) {
            if (!snippet || snippet.length < 10) continue;
            const index = fullNorm.indexOf(snippet);
            if (index >= 0) {
              const endIndex = index + snippet.length;
              for (const { span, start, end } of ranges) {
                if (end > index && start < endIndex) highlighted.add(span);
              }
              matched = true;
              break;
            }
          }

          if (!matched) {
            const compactRange = findCompactSnippetRangeInFullNorm(fullNorm, source.text || '', 15);
            if (compactRange) {
              for (const { span, start, end } of ranges) {
                if (end > compactRange.start && start < compactRange.end) highlighted.add(span);
              }
            }
          }
        }
      }

      for (const source of sourcesForPage) {
        const rects = getViewportRectsForSource(pageViewport, currentPageNumber, source);
        for (const span of spans) {
          const spanRect = span.getBoundingClientRect();
          const spanLeft = spanRect.left - pageRect.left;
          const spanTop = spanRect.top - pageRect.top;
          const spanRight = spanLeft + spanRect.width;
          const spanBottom = spanTop + spanRect.height;
          for (const rect of rects) {
            const rectRight = rect.left + rect.width;
            const rectBottom = rect.top + rect.height;
            if (!(rectRight < spanLeft || rect.left > spanRight) && !(rectBottom < spanTop || rect.top > spanBottom)) {
              highlighted.add(span);
              break;
            }
          }
        }
      }

      for (const span of highlighted) span.classList.add('citation-highlight-text');

      const hasCoordinateFallback = sourcesForPage.some((source) => getViewportRectsForSource(pageViewport, currentPageNumber, source).length > 0);
      if (!highlighted.size && hasCoordinateFallback) {
        const retries = retryRef.current.get(currentPageNumber) || 0;
        if (retries < 2) {
          retryRef.current.set(currentPageNumber, retries + 1);
          setTimeout(() => {
            if (!cancelled) applyTextAndCoordinateHighlights(pageViewport, currentPageNumber);
          }, retries === 0 ? 250 : 400);
        }
      } else {
        retryRef.current.delete(currentPageNumber);
      }
    };

    const renderPage = async () => {
      try {
        clearPageLayers();
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const pageEl = pageRef.current;
        const textLayer = textLayerRef.current;
        if (!canvas || !pageEl || !textLayer) return;

        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        pageEl.style.width = `${viewport.width}px`;
        pageEl.style.height = `${viewport.height}px`;
        textLayer.style.width = `${viewport.width}px`;
        textLayer.style.height = `${viewport.height}px`;

        renderTask = page.render({ canvasContext: context, viewport });
        await renderTask.promise;
        if (cancelled) return;

        const fallbackSources = sameDocSources.filter((source) => source.coordinates?.some((coord) => coord.page === pageNumber));
        drawCoordinateFallbackOverlays(pageEl, viewport, pageNumber, fallbackSources);

        const textContent = await page.getTextContent();
        if (cancelled) return;
        textLayer.replaceChildren();
        for (const item of textContent.items || []) {
          if (!item.str) continue;
          const transform = pdfjsLib.Util.transform(viewport.transform, item.transform);
          const fontHeight = Math.hypot(transform[2], transform[3]) || Math.abs(transform[3]) || 10;
          const span = document.createElement('span');
          span.textContent = item.str;
          span.style.left = `${transform[4]}px`;
          span.style.top = `${transform[5] - fontHeight}px`;
          span.style.fontSize = `${fontHeight}px`;
          span.style.transform = `scaleX(${item.width ? (viewport.scale * item.width) / Math.max(span.textContent.length * fontHeight * 0.5, 1) : 1})`;
          textLayer.appendChild(span);
        }
        applyTextAndCoordinateHighlights(viewport, pageNumber);
        if (!cancelled) setPageReady(true);
      } catch (error) {
        if (cancelled || error?.name === 'RenderingCancelledException') return;
        setPageReady(false);
        setRenderError(error?.message || 'Unable to render this PDF page.');
      }
    };

    renderPage();
    return () => {
      cancelled = true;
      renderTask?.cancel?.();
    };
  }, [pdfDoc, pageNumber, scale, sameDocSources]);

  const isDocumentLoading = Boolean(pdfUrl && !pdfDoc && !renderError);
  const isPageRendering = Boolean(pdfDoc && !pageReady && !renderError);
  const showLoadingPanel = isDocumentLoading || isPageRendering;

  if (renderError) {
    return <div className="context-pdf-error">{renderError}</div>;
  }

  return (
    <div className="pdfjs-viewer">
      <div className="pdfjs-toolbar">
        <button type="button" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={!pdfDoc || pageNumber <= 1}>Previous</button>
        <span>Page {pageNumber}{numPages ? ` / ${numPages}` : ''}</span>
        <button type="button" onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))} disabled={!pdfDoc || pageNumber >= numPages}>Next</button>
        <button type="button" onClick={() => setScale((s) => Math.max(0.7, Number((s - 0.1).toFixed(2))))} disabled={!pageReady}>-</button>
        <span>{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => setScale((s) => Math.min(2, Number((s + 0.1).toFixed(2))))} disabled={!pageReady}>+</button>
      </div>
      <div className={`pdfjs-scroll${showLoadingPanel ? ' pdfjs-scroll--loading' : ''}`}>
        <div className="pdfjs-page" ref={pageRef} aria-hidden={showLoadingPanel}>
          <canvas ref={canvasRef} />
          <div className="pdfjs-text-layer" ref={textLayerRef} />
        </div>
        {showLoadingPanel && (
          <div
            className={`pdfjs-loading-panel${isDocumentLoading ? ' pdfjs-loading-panel--document' : ' pdfjs-loading-panel--page'}`}
            aria-live="polite"
            aria-busy="true"
          >
            <div className="pdfjs-loading-spinner" aria-hidden="true" />
            <p className="pdfjs-loading-title">
              {isDocumentLoading ? 'Loading document' : 'Preparing page'}
            </p>
            {selectedSource?.serff_id && (
              <p className="pdfjs-loading-meta">
                {selectedSource.serff_id}
                {' · '}
                Page {pageNumber}
                {numPages ? ` of ${numPages}` : ''}
              </p>
            )}
            {isDocumentLoading && (
              <p className="pdfjs-loading-hint">
                Large PDFs can take a few moments. You can open the file in a new tab while this loads.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};